import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SockJS from 'sockjs-client';
import { Client } from '@stomp/stompjs';
import axios from 'axios';

const ChatComponent = () => {
  const { recipientId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const messagesEndRef = useRef(null);
  const chatBoxRef = useRef(null);
  const pageRef = useRef(0);
  const isPrependingRef = useRef(false);

  // Replace username with dynamic if needed
  const username = 'john_doe';

  // Reset inbox when recipient changes
  useEffect(() => {
    setMessages([]);
    setHasMore(true);
    pageRef.current = 0;
    if (client) {
      client.deactivate();
      setClient(null);
    }
  }, [recipientId]);

  // Fetch older messages with deduplication
  const fetchOldMessages = async () => {
    if (!hasMore || loadingOlder) return;
    setLoadingOlder(true);
    try {
      const pageToLoad = pageRef.current;
      const { data } = await axios.get(
        `https://localhost:8080/whatsapp-message/all?pageNumber=${pageToLoad}&pageSize=8&sortOrder=desc&recipientId=${recipientId}&username=${username}`
      );
      const newMessages = data.content.reverse();

      setHasMore(!data.lastPage);
      pageRef.current = data.pageNumber + 1;

      if (newMessages.length) {
        isPrependingRef.current = true;
        setMessages(prev => {
          const merged = [...newMessages, ...prev];
          const unique = merged.filter(
            (msg, idx, arr) => arr.findIndex(m => m.messageId === msg.messageId) === idx
          );
          return unique;
        });
      }
    } catch (err) {
      console.error('Fetch old messages error:', err);
    } finally {
      setLoadingOlder(false);
    }
  };

  // Set up WebSocket and load initial messages
  useEffect(() => {
    if (!recipientId) return;
    const socket = new SockJS('https://www.aadow.com:8080/ws');
    const stompClient = new Client({
      webSocketFactory: () => socket,
      onConnect: () => {
        stompClient.subscribe(`/topic/messages/${recipientId}`, ({ body }) => {
          const msg = JSON.parse(body);
          setMessages(prev => [...prev, msg]);
        });
      }
    });

    stompClient.activate();
    setClient(stompClient);

    (async () => {
      await fetchOldMessages();
      if (chatBoxRef.current) {
        chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
      }
    })();

    return () => stompClient.deactivate();
  }, [recipientId]);

  // Auto-scroll logic
  useEffect(() => {
    if (isPrependingRef.current) {
      isPrependingRef.current = false;
      return;
    }
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Infinite scroll for older messages
  useEffect(() => {
    const onScroll = async () => {
      if (!hasMore || loadingOlder) return;
      const box = chatBoxRef.current;
      if (!box) return;
      if (box.scrollTop < 10) {
        const prevHeight = box.scrollHeight;
        await fetchOldMessages();
        const newHeight = box.scrollHeight;
        box.scrollTop = newHeight - prevHeight;
      }
    };

    const box = chatBoxRef.current;
    box?.addEventListener('scroll', onScroll);
    return () => box?.removeEventListener('scroll', onScroll);
  }, [hasMore, loadingOlder]);

  // Send message
  const sendMessage = async () => {
    if (!messageInput.trim()) return;

    const tempMessageId = `${username}-${Date.now()}`;
    const localMessage = {
      conversationId: username,
      messageBody: messageInput,
      sentBy: 'business',
      username,
      messageId: tempMessageId,
      senderId: 'whatsapp',
      recipientId,
      messageType: 'text',
      billable: false,
      messageStatus: 'SENT',
      pricingModel: 'CBP',
      category: 'UTILITY'
    };

    if (client && client.connected) {
      client.publish({ destination: '/app/chat/send', body: JSON.stringify(localMessage) });
    }

    const messageData = {
      messaging_product: 'whatsapp',
      to: recipientId,
      type: 'text',
      text: { body: messageInput }
    };

    try {
      const response = await axios.post(
        'https://graph.facebook.com/v19.0/575578602301147/messages',
        messageData,
        { headers: { Authorization: `Bearer EAAXOdC6dZBF8BO9ZAIkCz6zxZCf2QvkFliNS8FITHVMuXJOcQVjUiFCuANY60DzKQwR1cagEmHjxHhq3N6nly70AAZAoDHduRihL0EWgBSlfjrw1vDXLMp2jDjXZAG1pyLrLeN6c47L9jFKjdoU4wrUTQSZCBai0Xxv3NcOGOBB71XcpKROtjO4wZCiZA1vqwnbR2gZDZD`, 'Content-Type': 'application/json' } }
      );
      if (response.status !== 200) console.error('❌ Failed:', response);
    } catch (error) {
      console.error('WhatsApp API error:', error?.response?.data || error.message);
    }

    setMessageInput('');
  };

  return (
    <div style={styles.container}>
      {/* Recipient selection buttons */}
      <div style={styles.recipientList}>
        <button onClick={() => navigate('/socket/919742709022')}>919742709022</button>
        <button onClick={() => navigate('/socket/918050201083')}>918050201083</button>
      </div>
      <h2 style={styles.header}>Chat with {recipientId}</h2>
      <div ref={chatBoxRef} style={styles.chatBox}>
        {loadingOlder && hasMore && <div style={styles.loader}>Loading...</div>}
        {!hasMore && <div style={styles.noMore}>No earlier messages</div>}
        {messages.map(msg => (
          <div key={msg.messageId} style={{
            ...styles.message,
            alignSelf: msg.sentBy === 'business' ? 'flex-end' : 'flex-start',
            backgroundColor: msg.sentBy === 'business' ? '#DCF8C6' : '#FFF'
          }}>
            <strong>{msg.sentBy === 'business' ? 'You' : 'Customer'}:</strong> {msg.messageBody}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>
      <div style={styles.inputContainer}>
        <input
          style={styles.input}
          value={messageInput}
          onChange={e => setMessageInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && sendMessage()}
          placeholder="Type a message..."
        />
        <button style={styles.button} onClick={sendMessage}>Send</button>
      </div>
    </div>
  );
};

const styles = {
  container: { width: '100%', maxWidth: 600, margin: '0 auto', fontFamily: 'Arial, sans-serif' },
  recipientList: { display: 'flex', justifyContent: 'center', margin: '10px 0', gap: '8px' },
  header: { textAlign: 'center', margin: '10px 0' },
  chatBox: {
    display: 'flex',
    flexDirection: 'column',
    height: '400px',
    overflowY: 'auto',
    border: '1px solid #ccc',
    padding: '10px',
    borderRadius: '8px',
    backgroundColor: '#f9f9f9',
    marginBottom: '10px'
  },
  loader: { textAlign: 'center', padding: '8px', color: '#555' },
  noMore: { textAlign: 'center', padding: '8px', color: '#888', fontStyle: 'italic' },
  message: { padding: '8px 12px', borderRadius: '20px', marginBottom: '8px', maxWidth: '75%', wordWrap: 'break-word', boxShadow: '0 1px 2px rgba(0,0,0,0.1)' },
  inputContainer: { display: 'flex' },
  input: { flex: 1, padding: '10px', borderRadius: '20px', border: '1px solid #ccc', marginRight: '8px' },
  button: { padding: '10px 16px', borderRadius: '20px', border: 'none', backgroundColor: '#4CAF50', color: 'white', cursor: 'pointer' }
};

export default ChatComponent;
